/**
 * Shop decor (content/catalog/decor.json), pure: the spots every shop interior offers, what fits
 * where, what the player owns (meta.decor) and where each piece is placed (progress.shops[].decor).
 * One owned piece fills at most one spot across all shops, so placing it elsewhere moves it. A
 * placement is predicted here and queued as a `decor` sync op; the next snapshot wins.
 */
import decorJson from '@mp/content/catalog/decor.json';
import { has, t } from '../i18n/index.js';
import type { PlayerState } from './state.js';

export type SpotType = 'hanging' | 'wall' | 'window' | 'counter' | 'floor';

export interface DecorDef {
  id: string;
  spot: SpotType;
  source: 'album' | 'rush' | 'store';
  priceCoins?: number;
  priceGems?: number;
}

export interface DecorSpot {
  id: string;
  type: SpotType;
}

export const DECOR_SPOTS: readonly DecorSpot[] = (decorJson as { spots: DecorSpot[] }).spots;
export const DECOR: readonly DecorDef[] = (decorJson as { decor: DecorDef[] }).decor;

const decorById = new Map(DECOR.map((d) => [d.id, d]));
const spotById = new Map(DECOR_SPOTS.map((s) => [s.id, s]));

export function decorDef(id: string): DecorDef | undefined {
  return decorById.get(id);
}

export function spotDef(id: string): DecorSpot | undefined {
  return spotById.get(id);
}

/** `decor.<id without decor_>.name` in the store strings; the id itself when a build lacks the key. */
export function decorName(id: string): string {
  const key = `decor.${id.replace(/^decor_/, '')}.name`;
  return has(key) ? t(key) : id;
}

/** Placed pieces of one shop: spot → decor id. */
export function placedIn(s: PlayerState, shopId: string): Record<string, string> {
  return { ...(s.shops[shopId]?.decor ?? {}) };
}

/** Where a piece is placed, if anywhere. */
export function placementOf(s: PlayerState, decorId: string): { shopId: string; spot: string } | null {
  for (const [shopId, shop] of Object.entries(s.shops)) for (const [spot, id] of Object.entries(shop.decor ?? {})) if (id === decorId) return { shopId, spot };
  return null;
}

export interface DecorChoice {
  id: string;
  /** in this very spot */
  here: boolean;
  /** placed in another spot (this shop or another): choosing it moves it here */
  elsewhere: { shopId: string; spot: string } | null;
}

/**
 * The decorate sheet of one spot: the owned pieces that fit it, the one in it first, then the
 * free ones, then the ones placed elsewhere; catalog order within each group.
 */
export function choicesFor(s: PlayerState, shopId: string, spotId: string): DecorChoice[] {
  const spot = spotDef(spotId);
  if (!spot) return [];
  const rank = (c: DecorChoice) => (c.here ? 0 : c.elsewhere ? 2 : 1);
  return DECOR.filter((d) => d.spot === spot.type && (s.decor[d.id] ?? 0) > 0)
    .map((d) => {
      const at = placementOf(s, d.id);
      const here = at !== null && at.shopId === shopId && at.spot === spotId;
      return { id: d.id, here, elsewhere: here ? null : at };
    })
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map(({ c }) => c);
}

export type DecorRefusal = 'unknown_spot' | 'unknown_decor' | 'not_owned' | 'wrong_spot';

/** What the decorate sheet (features/decor) answers: a piece for the spot (null: take it down), or "get more" in the store. */
export type DecorSheetResult = { decor: string | null } | { store: true } | undefined;

/**
 * Local prediction of the `decor` op: `decorId` null clears the spot; otherwise the piece leaves
 * whatever spot it filled (in any shop) and takes this one. The server applies the same rules.
 */
export function applyDecor(s: PlayerState, shopId: string, spotId: string, decorId: string | null): { ok: true } | { ok: false; reason: DecorRefusal } {
  const spot = spotDef(spotId);
  if (!spot) return { ok: false, reason: 'unknown_spot' };
  if (decorId !== null) {
    const def = decorDef(decorId);
    if (!def) return { ok: false, reason: 'unknown_decor' };
    if ((s.decor[decorId] ?? 0) < 1) return { ok: false, reason: 'not_owned' };
    if (def.spot !== spot.type) return { ok: false, reason: 'wrong_spot' };
    const at = placementOf(s, decorId);
    if (at) delete s.shops[at.shopId]!.decor![at.spot];
  }
  const shop = (s.shops[shopId] ??= { upgrades: {} });
  const decor = (shop.decor ??= {});
  if (decorId === null) delete decor[spotId];
  else decor[spotId] = decorId;
  return { ok: true };
}

/** Decor pieces that differ between two placements of a shop (spot → new id or null), for the scene to redraw. */
export function decorDiff(before: Record<string, string>, after: Record<string, string>): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const spot of new Set([...Object.keys(before), ...Object.keys(after)])) if (before[spot] !== after[spot]) out[spot] = after[spot] ?? null;
  return out;
}
